// enhancedTenseMapCategories.js
// Enhanced Tense Map (Nested-aware): data + helpers (+ optional nested UI component)

import React, { useState } from "react";

/* =========================
 * HIERARCHICAL TENSE MAP CATEGORIES
 * Matching the exact syntax map structure provided by user
 * ========================= */
export const HIERARCHICAL_TENSE_MAP_CATEGORIES = [
  {
    key: "future_t1",
    label: "Future t+1",
    icon: "🔮",
    color: "#3B82F6",
    description: "Future tenses and constructions",
    subcategories: [
      {
        key: "no_future",
        label: "No Future",
        icon: "📝",
        color: "#DC2626",
        description: "Opens to Future Tenses sub-map",
        courses: [
          "DO / S",
          "BE + ING",
          "WILL – Immediate Decision",
          "WILL – Distant Future", 
          "WILL – Conditional 1",
          "WOULD",
          "MOD ED",
          "TO BE GOING TO"
        ]
      },
      {
        key: "when_if_conditional",
        label: "When / If Conditional",
        icon: "❓",
        color: "#A855F7",
        description: "Conditional constructions",
        courses: [
          "Cond 0",
          "Cond 1", 
          "Cond 2",
          "Cond 3"
        ]
      }
    ]
  },
  {
    key: "present_t0",
    label: "Present t°",
    icon: "🎯",
    color: "#10B981",
    description: "Present tenses and constructions",
    subcategories: [
      {
        key: "do_s_tn",
        label: "DO / S tn",
        icon: "📝",
        color: "#059669",
        description: "Present Simple constructions",
        courses: [
          "Present Simple"
        ]
      },
      {
        key: "be_ing_now",
        label: "BE + ING (NOW!!!)",
        icon: "⚡",
        color: "#06B6D4",
        description: "Present Continuous constructions",
        courses: [
          "Present Continuous"
        ]
      },
      {
        key: "have_en_present",
        label: "HAVE EN",
        icon: "✅",
        color: "#EC4899",
        description: "Present Perfect constructions",
        courses: [
          "Present Perfect"
        ]
      },
      {
        key: "mod_present",
        label: "MOD (Present Modals)",
        icon: "🎭",
        color: "#8B5CF6",
        description: "Present modal verbs",
        courses: [
          "Must",
          "Shall",
          "Should", 
          "Will",
          "Would",
          "Can",
          "Could",
          "May",
          "Might"
        ]
      }
    ]
  },
  {
    key: "past_t1",
    label: "Past t-1",
    icon: "📚",
    color: "#F59E0B",
    description: "Past tenses and constructions",
    subcategories: [
      {
        key: "did_ed_t1",
        label: "DID / ED t-1 tn",
        icon: "📖",
        color: "#D97706",
        description: "Past Simple constructions",
        courses: [
          "Past Simple"
        ]
      },
      {
        key: "was_were_ing",
        label: "WAS / WERE + ING",
        icon: "🔄",
        color: "#0891B2",
        description: "Past Continuous constructions",
        courses: [
          "Past Continuous"
        ]
      },
      {
        key: "had_en_past",
        label: "HAD EN",
        icon: "⏰",
        color: "#EF4444",
        description: "Past Perfect constructions",
        courses: [
          "Past Perfect"
        ]
      },
      {
        key: "mod_ed_past",
        label: "MOD ED (Past Modals)",
        icon: "🎪",
        color: "#7C3AED",
        description: "Past modal constructions",
        courses: [
          "Must ED",
          "Should t-n",
          "Would t-n",
          "Would t+1",
          "Could t-n",
          "Was / Were Supposed To",
          "Had To",
          "Was / Were Able To",
          "Used To",
          "Might",
          "Was / Were Allowed To"
        ]
      }
    ]
  },
  {
    key: "be_en_passive",
    label: "BE EN (Cross-cutting)",
    icon: "🔄",
    color: "#F97316",
    description: "Passive Voice constructions",
    subcategories: [
      {
        key: "passive_voice",
        label: "Passive Voice",
        icon: "🔀",
        color: "#F97316",
        description: "All passive voice constructions",
        courses: [
          "Passive Voice",
          "Present Simple Passive",
          "Past Simple Passive",
          "Future Simple Passive",
          "Present Continuous Passive",
          "Past Continuous Passive",
          "Present Perfect Passive",
          "Past Perfect Passive"
        ]
      }
    ]
  }
];

/* =========================
 * HIERARCHICAL COURSE CATEGORIZATION FUNCTION
 * ========================= */
export function categorizeCoursesWithHierarchicalTenseMap(courseList) {
  const lower = (s) => (s || "").toLowerCase();
  const normalize = (s) => lower(s).replace(/[+()]/g, " ").replace(/\s+/g, " ").trim();
  
  const categorizedCourses = {};
  const uncategorized = [];
  
  // Initialize categories
  for (const category of HIERARCHICAL_TENSE_MAP_CATEGORIES) {
    categorizedCourses[category.key] = {
      ...category,
      subcategories: category.subcategories.map(sub => ({
        ...sub,
        matchedCourses: []
      }))
    };
  }
  
  // Match courses to subcategories
  for (const course of courseList) {
    const courseText = normalize(course.course_item);
    let matched = false;
    
    for (const category of HIERARCHICAL_TENSE_MAP_CATEGORIES) {
      for (const subcategory of category.subcategories) {
        for (const expectedCourse of subcategory.courses) {
          const expectedText = normalize(expectedCourse);
          
          // Exact match
          if (courseText === expectedText) {
            categorizedCourses[category.key].subcategories
              .find(sub => sub.key === subcategory.key)
              .matchedCourses.push({
                ...course,
                matchScore: 100,
                matchedExpected: expectedCourse
              });
            matched = true;
            break;
          }
          
          // Partial match
          if (courseText.includes(expectedText) || expectedText.includes(courseText)) {
            const score = Math.min(courseText.length, expectedText.length) / 
                         Math.max(courseText.length, expectedText.length) * 80;
            
            categorizedCourses[category.key].subcategories
              .find(sub => sub.key === subcategory.key)
              .matchedCourses.push({
                ...course,
                matchScore: score,
                matchedExpected: expectedCourse
              });
            matched = true;
            break;
          }
        }
        if (matched) break;
      }
      if (matched) break;
    }
    
    if (!matched) {
      uncategorized.push(course);
    }
  }
  
  return { categorizedCourses, uncategorized };
}

/* =========================
 * 1) SYNTAX MAP NAVIGATION CATEGORIES
 * Enhanced to match actual navigation paths from the syntax map
 * ========================= */
export const SYNTAX_MAP_NAVIGATION_CATEGORIES = [
  {
    key: "when_if_cond",
    label: "Conditional Constructions",
    navigationPath: "WHEN / IF COND",
    position: { x: "conditional-zone", y: "top" },
    color: "#A855F7",
    description: "Courses accessed via WHEN/IF COND button - conditional situations",
    icon: "COND",
    items: [
      "COND 0",
      "COND 1", 
      "COND 2",
      "COND 3",
      {
        label: "COND 0 (n) - WHEN / IF is the condition",
        children: [
          "When/If + present simple, present simple"
        ]
      },
      {
        label: "COND 1 (1+) - Future condition", 
        children: [
          "As soon as",
          "While",
          "When",
          "The moment",
          "Before",
          "After",
          "HAVE EN",
          "must",
          "will",
          "can",
          "may",
          "have to",
          "be able to",
          "could",
          "might"
        ]
      },
      {
        label: "COND 2 (t-) - Imaginary condition",
        children: [
          "If",
          "Could ED",
          "Would",
          "Could",
          "Might",
          "Have to",
          "Be able to"
        ]
      },
      {
        label: "COND 3 (t-1) - Imaginary condition in t-1",
        children: [
          "If + could (ED) + (HAVE EN) + HAD EN",
          "Would",
          "Could", 
          "Might",
          "HAVE + EN"
        ]
      }
    ],
  },
  {
    key: "mod_ed",
    label: "Modal + Past Constructions", 
    navigationPath: "MOD ED",
    position: { x: "center", y: "modal-past" },
    color: "#8B5CF6",
    description: "Courses accessed via MOD ED button - modals with past reference",
    icon: "MOD ED",
    items: [
      "Must t-1",
      "Would t-n",
      "Should (advice)",
      "Could t-1(-n)",
      "Might future in the past (possibility)",
      {
        label: "Present t° Context",
        children: [
          "must (right point of view of the subject)",
          "was/were supposed to (not saying if the action was carried out)",
          "had to (t-1(-n)) necessarily did occur"
        ]
      },
      {
        label: "Past Reference",
        children: [
          "Would t-n (strong relation between subject and verb)",
          "Would t-1 (future in the past tense)",
          "used to (t-n)",
          "ED"
        ]
      },
      {
        label: "Ability/Possibility Past",
        children: [
          "Could t-1(-n) (volition capacity occurred general capacity)",
          "was/were able to (t-1) necessarily did occur",
          "Might future in the past (possibility)",
          "was/were allowed to (permission)"
        ]
      }
    ],
  },
  {
    key: "had_en",
    label: "Past Perfect Constructions",
    navigationPath: "HAD EN", 
    position: { x: "auxiliary-column", y: "past" },
    color: "#EF4444",
    description: "Courses accessed via HAD EN button - past perfect tenses",
    icon: "HAD EN",
    items: [
      "Past Perfect",
      "Had + past participle",
      "Past Perfect Continuous",
      "Had been + ing",
      {
        label: "Past Perfect Simple",
        children: [
          "Had + past participle",
          "Past actions completed before another past action"
        ]
      },
      {
        label: "Past Perfect Continuous", 
        children: [
          "Had been + ing",
          "Past actions in progress before another past action"
        ]
      }
    ],
  },
  {
    key: "have_en",
    label: "Perfect Constructions",
    navigationPath: "HAVE EN",
    position: { x: "auxiliary-column", y: "center" },
    color: "#EC4899",
    description: "Courses accessed via HAVE EN button - present perfect tenses",
    icon: "HAVE EN",
    items: [
      "Present Perfect",
      "Present Perfect Continuous", 
      "Have + past participle",
      "Have been + ing",
      {
        label: "Present Perfect Simple",
        children: [
          "Have/Has + past participle",
          "Actions completed with present relevance"
        ]
      },
      {
        label: "Present Perfect Continuous",
        children: [
          "Have/Has been + ing", 
          "Actions started in past continuing to present"
        ]
      }
    ],
  },
  {
    key: "be_en_passive",
    label: "Passive Voice",
    navigationPath: "BE EN",
    position: { x: "auxiliary-column", y: "left" },
    color: "#F97316", 
    description: "Courses accessed via BE EN button - passive voice constructions",
    icon: "BE EN",
    items: [
      "Passive Voice",
      "Present Simple Passive",
      "Past Simple Passive", 
      "Future Simple Passive",
      "Present Continuous Passive",
      "Past Continuous Passive",
      "Present Perfect Passive",
      "Past Perfect Passive",
      {
        label: "Simple Passives",
        children: [
          "Am/Is/Are + past participle",
          "Was/Were + past participle",
          "Will be + past participle"
        ]
      },
      {
        label: "Complex Passives",
        children: [
          "Am/Is/Are being + past participle",
          "Was/Were being + past participle", 
          "Have/Has been + past participle",
          "Had been + past participle"
        ]
      }
    ],
  },
  {
    key: "be_ing",
    label: "Continuous Constructions",
    navigationPath: "BE ING", 
    position: { x: "auxiliary-column", y: "right" },
    color: "#06B6D4",
    description: "Courses accessed via BE ING button - continuous/progressive forms",
    icon: "BE ING",
    items: [
      "Present Continuous",
      "Past Continuous",
      "Future Continuous",
      "Present Perfect Continuous", 
      "Past Perfect Continuous",
      "Future Perfect Continuous",
      {
        label: "Simple Continuous",
        children: [
          "Am/Is/Are + ing",
          "Was/Were + ing",
          "Will be + ing"
        ]
      },
      {
        label: "Perfect Continuous",
        children: [
          "Have/Has been + ing",
          "Had been + ing",
          "Will have been + ing"
        ]
      }
    ],
  },
  {
    key: "no_future",
    label: "No Future Constructions", 
    navigationPath: "NO FUTURE",
    position: { x: "top-center", y: "future" },
    color: "#DC2626",
    description: "Courses accessed via NO FUTURE button - present alternatives to future",
    icon: "NO FUTURE",
    items: [
      "DO / S",
      "BE ING", 
      "WILL (30%)",
      "will mod",
      "WOULD",
      "WOULD MOD S (36%)",
      "be going to (18%)",
      "TO BE TO (14%)",
      "immediate decision (20%)",
      {
        label: "Present Alternatives",
        children: [
          "Present Simple for future",
          "Present Continuous for future",
          "Going to + infinitive"
        ]
      }
    ],
  },
  {
    key: "to_be_going_to",
    label: "Future Planning",
    navigationPath: "TO BE GOING TO",
    position: { x: "future-zone", y: "planning" },
    color: "#3B82F6",
    description: "Courses accessed via TO BE GOING TO button - planned future actions", 
    icon: "GOING TO",
    items: [
      "TO BE GOING TO",
      "Be going to + infinitive",
      "Was/Were going to + infinitive",
      {
        label: "Future Plans",
        children: [
          "Am/Is/Are going to + infinitive",
          "Planned future actions",
          "Future predictions based on evidence"
        ]
      },
      {
        label: "Past Plans",
        children: [
          "Was/Were going to + infinitive",
          "Past intentions that may not have happened"
        ]
      }
    ],
  },
  {
    key: "immediate_decision", 
    label: "Immediate Decisions",
    navigationPath: "Immediate Decision",
    position: { x: "decision-zone", y: "immediate" },
    color: "#10B981",
    description: "Courses for immediate decision-making expressions",
    icon: "NOW",
    items: [
      "immediate decision",
      "Will + infinitive (spontaneous)",
      "Present decisions",
      {
        label: "Spontaneous Decisions",
        children: [
          "Will + infinitive (decided now)",
          "Immediate responses",
          "On-the-spot choices"
        ]
      }
    ],
  },
  {
    key: "distant_future",
    label: "Distant Future",
    navigationPath: "Distant Future", 
    position: { x: "future-zone", y: "distant" },
    color: "#6366F1",
    description: "Courses for distant future expressions",
    icon: "FAR",
    items: [
      "distant future",
      "Future Perfect",
      "Future Perfect Continuous",
      {
        label: "Far Future",
        children: [
          "Will have + past participle",
          "Will have been + ing",
          "Long-term future completion"
        ]
      }
    ],
  }
];

// Backward compatibility - keep original categories for existing functionality
export const TENSE_MAP_CATEGORIES = [
  {
    key: "future_t1",
    label: "Future t+1",
    position: { x: "top", y: "right" },
    color: "#3B82F6",
    description: "Questions about future actions and intentions",
    items: [
      "Will (Future Simple)",
      "Will Mod",
      "Future",
      "Distant Future",
      "Would t+1",
      "Could t+1",
      {
        label: "NO FUTURE",
        children: [
          "DO / S",
          "BE ING",
          "WILL (30%)",
          "will mod",
          "WOULD",
          "WOULD MOD S (36%)",
          "be going to (18%)",
          "TO BE TO (14%)",
          "immediate decision (20%)",
        ],
      },
      {
        label: "WHEN / IF COND",
        children: ["COND 0", "COND 1", "COND 2", "COND 3"],
      },
    ],
  },
  {
    key: "present_t0",
    label: "Present t°",
    position: { x: "center", y: "center" },
    color: "#10B981",
    description: "Questions about current states and ongoing actions",
    items: [
      "Present Simple",
      "Present Continuous",
      "Present Perfect",
      "Present Perfect Continuous",
      "DO",
      "IS",
      "BE + ING",
    ],
  },
  {
    key: "past_t1",
    label: "Past t-1",
    position: { x: "bottom-left", y: "left" },
    color: "#F59E0B",
    description: "Questions about recent past actions",
    items: [
      "Past Simple",
      "Past Continuous",
      "DID",
      "WAS/WERE",
      "Had To",
      "Was/Were Supposed To",
    ],
  },
  {
    key: "past_t2",
    label: "Past t-2",
    position: { x: "bottom", y: "far-left" },
    color: "#EF4444",
    description: "Questions about distant past or past perfect actions",
    items: ["Past Perfect", "Had EN", "Should have + past participle"],
  },
  {
    key: "mod",
    label: "MOD",
    position: { x: "center", y: "modal-row" },
    color: "#8B5CF6",
    description: "Questions about possibility, ability, permission, obligation",
    items: [
      {
        label: "MOD",
        children: [
          "MUST",
          "SHALL",
          "SHOULD",
          "WILL",
          "WOULD",
          "CAN",
          "COULD",
          "MAY",
          "MIGHT",
        ],
      },
      "Must ED",
      "Should",
      "Would",
      "Shall",
      "Can",
      "Could",
      "May",
      "Might",
    ],
  },
  {
    key: "have_en",
    label: "HAVE EN",
    position: { x: "auxiliary-column", y: "center" },
    color: "#EC4899",
    description: "Questions using HAVE + past participle constructions",
    items: ["HAVE EN", "HAVE EN + DID/ED", "Had EN"],
  },
  {
    key: "be_en",
    label: "BE EN",
    position: { x: "auxiliary-column", y: "left" },
    color: "#F97316",
    description: "Questions using BE + past participle (passive voice)",
    items: ["Passive Voice", "BE EN"],
  },
  {
    key: "be_ing",
    label: "BE ING",
    position: { x: "auxiliary-column", y: "right" },
    color: "#06B6D4",
    description: "Questions using BE + present participle (continuous forms)",
    items: ["BE + ING", "ED + BE ING"],
  },
  {
    key: "when_if_cond",
    label: "WHEN / IF COND",
    position: { x: "conditional-zone", y: "top" },
    color: "#8B5CF6",
    description: "Questions about conditional situations",
    items: ["COND 0", "COND 1", "COND 2", "COND 3", "When/If COND"],
  },
  {
    key: "then_iii",
    label: "THEN III",
    position: { x: "conditional-zone", y: "right" },
    color: "#EAB308",
    description: "Questions about conditional results",
    items: ["Then III"],
  },
  {
    key: "no_future",
    label: "NO FUTURE",
    position: { x: "top-center", y: "future" },
    color: "#DC2626",
    description: "Questions about lack of future possibility",
    items: ["ED"], // placeholder leaf in data model; real children live under future_t1 → NO FUTURE
    aliasOf: { category: "future_t1", path: ["NO FUTURE"] },
  },
  {
    key: "ed",
    label: "ED",
    position: { x: "past-zone", y: "bottom" },
    color: "#B45309",
    description: "Questions about past participle forms",
    items: ["ED"],
  },
];

/* ===== Helpers for nested matching, coordinates, indicators, optional UI (unchanged) ===== */
const lower = (s) => (s || "").toLowerCase();
const normalize = (s) =>
  lower(s).replace(/[+()]/g, " ").replace(/\s+/g, " ").trim();

function flattenItems(items, path = []) {
  const out = [];
  for (const it of items || []) {
    if (typeof it === "string")
      out.push({ label: it, path: path.slice(), isLeaf: true });
    else if (it && typeof it === "object" && it.label) {
      out.push({ label: it.label, path: path.slice(), isLeaf: false });
      out.push(...flattenItems(it.children || [], path.concat(it.label)));
    }
  }
  return out;
}
function ensurePath(rootNode, path = []) {
  let node = rootNode;
  for (const seg of path) {
    node.children ||= {};
    node.children[seg] ||= { label: seg, children: {}, courses: [] };
    node = node.children[seg];
  }
  return node;
}
function findCourseNodesInTree(rootNode, courseId, acc = [], path = []) {
  if (!rootNode) return acc;
  if (Array.isArray(rootNode.courses)) {
    for (const c of rootNode.courses) {
      if (String(c.course_id) === String(courseId))
        acc.push({ node: rootNode, path: path.slice(), course: c });
    }
  }
  const children = rootNode.children || {};
  for (const [label, child] of Object.entries(children)) {
    findCourseNodesInTree(child, courseId, acc, path.concat(label));
  }
  return acc;
}

// Enhanced categorization function using syntax map navigation paths
export function categorizeCoursesWithSyntaxMapNavigation(courseList) {
  const positionMap = {};
  const categoriesTree = {};
  
  for (const cat of SYNTAX_MAP_NAVIGATION_CATEGORIES) {
    categoriesTree[cat.key] = {
      label: cat.label,
      navigationPath: cat.navigationPath,
      position: cat.position,
      color: cat.color,
      description: cat.description,
      icon: cat.icon,
      children: {},
      courses: [],
    };
    positionMap[cat.key] = {
      position: cat.position,
      color: cat.color,
      description: cat.description,
      label: cat.label,
      navigationPath: cat.navigationPath,
      icon: cat.icon,
    };
  }

  const flatIndex = {};
  for (const cat of SYNTAX_MAP_NAVIGATION_CATEGORIES)
    flatIndex[cat.key] = flattenItems(cat.items);

  for (const course of courseList) {
    const itemText = normalize(course.course_item);
    let best = null;

    for (const cat of SYNTAX_MAP_NAVIGATION_CATEGORIES) {
      const flattened = flatIndex[cat.key] || [];
      for (const node of flattened) {
        const target = normalize(node.label);
        if (itemText === target) {
          best = {
            cat,
            nodePath: node.path,
            nodeLabel: node.label,
            score: 100,
          };
          break;
        }
        if (itemText.includes(target) && target.length) {
          const score = (target.length / itemText.length) * 80;
          if (!best || score > best.score)
            best = { cat, nodePath: node.path, nodeLabel: node.label, score };
        }
        if (target.includes(itemText) && itemText.length) {
          const score = (itemText.length / target.length) * 60;
          if (!best || score > best.score)
            best = { cat, nodePath: node.path, nodeLabel: node.label, score };
        }
      }
      if (best?.score === 100) break;
    }

    if (best) {
      const root = categoriesTree[best.cat.key];
      const node = ensurePath(root, best.nodePath);
      node.courses ||= [];
      node.courses.push({
        ...course,
        tenseMapPosition: positionMap[best.cat.key].position,
        tenseMapColor: positionMap[best.cat.key].color,
        tenseMapDescription: positionMap[best.cat.key].description,
        navigationPath: positionMap[best.cat.key].navigationPath,
        navigationIcon: positionMap[best.cat.key].icon,
        matchedLabel: best.nodeLabel,
        matchedPath: best.nodePath,
        matchScore: best.score,
      });
    } else {
      categoriesTree.uncategorized ||= {
        label: "Uncategorized",
        navigationPath: "Manual Review Required",
        position: { x: "unknown", y: "unknown" },
        color: "#6B7280",
        description: "Courses that need manual syntax map categorization",
        icon: "?",
        children: {},
        courses: [],
      };
      categoriesTree.uncategorized.courses.push({
        ...course,
        tenseMapPosition: { x: "unknown", y: "unknown" },
        tenseMapColor: "#6B7280",
        tenseMapDescription: "Needs manual categorization",
        navigationPath: "Manual Review Required",
        navigationIcon: "?",
        matchedLabel: null,
        matchedPath: [],
        matchScore: 0,
      });
    }
  }

  const categoriesFlat = {};
  function collect(node, path, out) {
    if (node.courses?.length)
      for (const c of node.courses) out.push({ ...c, matchedPath: path });
    for (const [label, child] of Object.entries(node.children || {}))
      collect(child, path.concat(label), out);
  }
  for (const [catKey, root] of Object.entries(categoriesTree)) {
    const bucket = [];
    collect(root, [], bucket);
    categoriesFlat[catKey] = bucket;
  }

  return { categories: categoriesFlat, tree: categoriesTree, positionMap };
}

// Keep original function for backward compatibility
export function categorizeCoursesWithTenseMap(courseList) {
  const positionMap = {};
  const categoriesTree = {};
  for (const cat of TENSE_MAP_CATEGORIES) {
    categoriesTree[cat.key] = {
      label: cat.label,
      position: cat.position,
      color: cat.color,
      description: cat.description,
      children: {},
      courses: [],
    };
    positionMap[cat.key] = {
      position: cat.position,
      color: cat.color,
      description: cat.description,
      label: cat.label,
    };
  }

  const flatIndex = {};
  for (const cat of TENSE_MAP_CATEGORIES)
    flatIndex[cat.key] = flattenItems(cat.items);

  for (const course of courseList) {
    const itemText = normalize(course.course_item);
    let best = null;

    for (const cat of TENSE_MAP_CATEGORIES) {
      const flattened = flatIndex[cat.key] || [];
      for (const node of flattened) {
        const target = normalize(node.label);
        if (itemText === target) {
          best = {
            cat,
            nodePath: node.path,
            nodeLabel: node.label,
            score: 100,
          };
          break;
        }
        if (itemText.includes(target) && target.length) {
          const score = (target.length / itemText.length) * 80;
          if (!best || score > best.score)
            best = { cat, nodePath: node.path, nodeLabel: node.label, score };
        }
        if (target.includes(itemText) && itemText.length) {
          const score = (itemText.length / target.length) * 60;
          if (!best || score > best.score)
            best = { cat, nodePath: node.path, nodeLabel: node.label, score };
        }
      }
      if (best?.score === 100) break;
    }

    if (best) {
      const root = categoriesTree[best.cat.key];
      const node = ensurePath(root, best.nodePath);
      node.courses ||= [];
      node.courses.push({
        ...course,
        tenseMapPosition: positionMap[best.cat.key].position,
        tenseMapColor: positionMap[best.cat.key].color,
        tenseMapDescription: positionMap[best.cat.key].description,
        matchedLabel: best.nodeLabel,
        matchedPath: best.nodePath,
        matchScore: best.score,
      });
    } else {
      categoriesTree.uncategorized ||= {
        label: "Uncategorized",
        position: { x: "unknown", y: "unknown" },
        color: "#6B7280",
        description: "Courses that need manual categorization",
        children: {},
        courses: [],
      };
      categoriesTree.uncategorized.courses.push({
        ...course,
        tenseMapPosition: { x: "unknown", y: "unknown" },
        tenseMapColor: "#6B7280",
        tenseMapDescription: "Needs manual categorization",
        matchedLabel: null,
        matchedPath: [],
        matchScore: 0,
      });
    }
  }

  const categoriesFlat = {};
  function collect(node, path, out) {
    if (node.courses?.length)
      for (const c of node.courses) out.push({ ...c, matchedPath: path });
    for (const [label, child] of Object.entries(node.children || {}))
      collect(child, path.concat(label), out);
  }
  for (const [catKey, root] of Object.entries(categoriesTree)) {
    const bucket = [];
    collect(root, [], bucket);
    categoriesFlat[catKey] = bucket;
  }

  return { categories: categoriesFlat, tree: categoriesTree, positionMap };
}

export function getTenseMapCoordinates(category) {
  const coordinateMap = {
    future_t1: { x: 80, y: 20 },
    present_t0: { x: 50, y: 50 },
    past_t1: { x: 20, y: 70 },
    past_t2: { x: 10, y: 85 },
    mod: { x: 50, y: 30 },
    have_en: { x: 70, y: 45 },
    be_en: { x: 35, y: 45 },
    be_ing: { x: 65, y: 55 },
    when_if_cond: { x: 75, y: 25 },
    then_iii: { x: 85, y: 35 },
    no_future: { x: 50, y: 15 },
    ed: { x: 25, y: 80 },
  };
  return coordinateMap[category] || { x: 50, y: 50 };
}
export function getTenseMapNodeCoordinates(categoryKey, path = []) {
  const base = getTenseMapCoordinates(categoryKey);
  if (!path.length) return base;
  const str = path.join("/");
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  const depth = path.length,
    spread = 4;
  const jitterX = ((hash % 9) - 4) * 1.2,
    jitterY = ((((hash / 9) | 0) % 9) - 4) * 1.2;
  const x = Math.max(0, Math.min(100, base.x + depth * spread + jitterX));
  const y = Math.max(0, Math.min(100, base.y + depth * spread + jitterY));
  return { x, y };
}
export function generateTenseMapIndicator(courseIds, courseList) {
  const { tree } = categorizeCoursesWithTenseMap(courseList);
  const indicators = [];
  const ids = new Set(courseIds.map(String));
  function find(root, courseId, acc = [], path = []) {
    if (root?.courses)
      for (const c of root.courses)
        if (String(c.course_id) === String(courseId))
          acc.push({ path: path.slice(), course: c });
    for (const [label, child] of Object.entries(root?.children || {}))
      find(child, courseId, acc, path.concat(label));
    return acc;
  }
  for (const [categoryKey, root] of Object.entries(tree)) {
    if (!root || typeof root !== "object") continue;
    for (const id of ids) {
      const hits = find(root, id, []);
      for (const { path, course } of hits) {
        const coords = getTenseMapNodeCoordinates(categoryKey, path);
        indicators.push({
          category: categoryKey,
          path,
          nodeLabel: path[path.length - 1] || root.label,
          position: coords,
          color: course.tenseMapColor,
          description: course.tenseMapDescription,
          courseName: course.course_item,
          matchScore: course.matchScore,
        });
      }
    }
  }
  return indicators;
}

/* Optional nested chip UI exported for reuse */
const slug = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
const keyFor = (catKey, path, label) =>
  [catKey, ...path, label].map(slug).join("|");
function Chip({ checked, onChange, children }) {
  return (
    <label className="tm-chip">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span>{children}</span>
    </label>
  );
}
function GroupHeader({ open, onToggle, label }) {
  return (
    <button type="button" className="tm-group" onClick={onToggle}>
      <span className={`tm-caret ${open ? "open" : ""}`} />
      <span className="tm-group-label">{label}</span>
    </button>
  );
}
function Items({
  cat,
  items,
  path,
  selected,
  toggle,
  openGroups,
  setOpenGroups,
}) {
  return (
    <div className="tm-items">
      {items.map((it) =>
        typeof it === "string" ? (
          <Chip
            key={keyFor(cat.key, path, it)}
            checked={selected.has(keyFor(cat.key, path, it))}
            onChange={() => toggle(keyFor(cat.key, path, it))}
          >
            {it}
          </Chip>
        ) : (
          <div className="tm-group-block" key={keyFor(cat.key, path, it.label)}>
            <GroupHeader
              open={openGroups.has(keyFor(cat.key, path, it.label))}
              label={it.label}
              onToggle={() => {
                const id = keyFor(cat.key, path, it.label);
                const next = new Set(openGroups);
                next.has(id) ? next.delete(id) : next.add(id);
                setOpenGroups(next);
              }}
            />
            {openGroups.has(keyFor(cat.key, path, it.label)) && (
              <div className="tm-children">
                <Items
                  cat={cat}
                  items={it.children || []}
                  path={[...path, it.label]}
                  selected={selected}
                  toggle={toggle}
                  openGroups={openGroups}
                  setOpenGroups={setOpenGroups}
                />
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}
export function TenseMapSelect({ selected, onChange }) {
  const [openGroups, setOpenGroups] = useState(new Set());
  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(next);
  };
  return (
    <div className="tm-wrap">
      {TENSE_MAP_CATEGORIES.map((cat) => (
        <section className="tm-category" key={cat.key}>
          <header className="tm-cat-header">
            <span className="tm-dot" style={{ background: cat.color }} />
            <div className="tm-cat-text">
              <div className="tm-cat-title">{cat.label}</div>
              <div className="tm-cat-desc">{cat.description}</div>
            </div>
          </header>
          <Items
            cat={cat}
            items={cat.items}
            path={[]}
            selected={selected}
            toggle={toggle}
            openGroups={openGroups}
            setOpenGroups={setOpenGroups}
          />
        </section>
      ))}
    </div>
  );
}
